import * as z from 'zod';

const vrtLookupLimitSchema = z.number().int().min(1).max(25).default(10);
const vrtLookupPageSchema = z.number().int().min(1).default(1);
const vrtDateRangeSchema = z.string().trim().min(1).optional();
const vrtCsvSchema = z.string().trim().min(1).optional();
const vrtTeamIdSchema = z.string().uuid().optional();

export const vrtPaginatedLookupPayloadSchema = z.object({
  data: z.array(z.unknown()),
  pagination: z.object({
    limit: z.number().int(),
    nextPage: z.number().int().optional(),
    page: z.number().int(),
    total: z.number().int(),
    totalPage: z.number().int(),
  }),
});

export const vrtPaginatedLookupOutputSchema =
  vrtPaginatedLookupPayloadSchema.extend({
    schemaVersion: z.literal(1),
  });

export const vrtListingsLookupSchema = z.object({
  activeOwnership: z.boolean().optional(),
  includeChildren: z.boolean().optional(),
  issue: z
    .enum([
      'all',
      'any',
      'missingOwnershipOrDeactivation',
      'missingFeesAndCommission',
      'missingOpeningBalance',
    ])
    .optional(),
  limit: vrtLookupLimitSchema,
  page: vrtLookupPageSchema,
  parent: z.union([z.string().uuid(), z.literal('null')]).optional(),
  search: z.string().trim().min(1).optional(),
  status: z.enum(['active', 'inactive']).optional(),
  teamId: vrtTeamIdSchema,
});

export const vrtReservationsLookupSchema = z.object({
  bookingChannel: z.string().trim().min(1).optional(),
  date: vrtDateRangeSchema,
  dateField: z
    .enum([
      'bookedAt',
      'cancelledAt',
      'checkIn',
      'checkOut',
      'payment',
      'intersection',
    ])
    .optional(),
  generalLedgerStatus: z.enum(['active', 'inactive']).optional(),
  ids: vrtCsvSchema,
  includeLines: z.boolean().optional(),
  limit: vrtLookupLimitSchema,
  listingId: z.string().trim().min(1).optional(),
  page: vrtLookupPageSchema,
  paidStatus: z
    .enum([
      'unpaid',
      'underpaid',
      'overpaid',
      'paid',
      '!unpaid',
      '!underpaid',
      '!overpaid',
      '!paid',
    ])
    .optional(),
  search: z.string().trim().min(1).optional(),
  shortRefs: vrtCsvSchema,
  status: z.enum(['booked', 'canceled', 'inactive']).optional(),
  teamId: vrtTeamIdSchema,
});

export const vrtTransactionsLookupSchema = z.object({
  accountId: z.string().trim().min(1).optional(),
  amount: z.string().trim().min(1).optional(),
  contactId: z.string().uuid().optional(),
  dateRange: vrtDateRangeSchema,
  ids: vrtCsvSchema,
  includeLines: z.boolean().optional(),
  limit: vrtLookupLimitSchema,
  listingIds: vrtCsvSchema,
  page: vrtLookupPageSchema,
  paidAtRange: vrtDateRangeSchema,
  paidStatus: z.enum(['unpaid', 'underpaid', 'overpaid', 'paid']).optional(),
  reconciled: z.union([z.boolean(), z.literal('candidate')]).optional(),
  search: z.string().trim().min(1).optional(),
  shortRefs: vrtCsvSchema,
  status: z.enum(['active', 'inactive']).optional(),
  teamId: vrtTeamIdSchema,
  type: z.enum(['deposit', 'expense', 'transfer', 'payout']).optional(),
  uniqueRefs: vrtCsvSchema,
});

export const vrtAccountsLookupSchema = z.object({
  bankingCategory: z.enum(['trust', 'operating', 'external']).optional(),
  classification: z
    .enum([
      'asset',
      'liability',
      'equity',
      'revenue',
      'costOfRevenue',
      'expense',
    ])
    .optional(),
  limit: vrtLookupLimitSchema,
  page: vrtLookupPageSchema,
  reconciled: z.boolean().optional(),
  search: z.string().trim().min(1).optional(),
  status: z.enum(['active', 'inactive']).optional(),
  teamId: vrtTeamIdSchema,
  type: z.enum(['ledger', 'bank', 'recurringFee', 'nonPosting']).optional(),
});

export const vrtStatementsLookupSchema = z.object({
  dateRange: vrtDateRangeSchema,
  isDateRangeEndInclusive: z.boolean().optional(),
  limit: vrtLookupLimitSchema,
  listingIds: vrtCsvSchema,
  month: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .optional(),
  ownerIds: vrtCsvSchema,
  page: vrtLookupPageSchema,
  periodIds: vrtCsvSchema,
  recurringFeeIds: vrtCsvSchema,
  search: z.string().trim().min(1).optional(),
  statementIds: vrtCsvSchema,
  status: z.enum(['draft', 'inReview', 'published', 'all']).optional(),
  teamId: vrtTeamIdSchema,
  year: z.number().int().min(1900).max(2100).optional(),
});

export const vrtJournalEntriesLookupSchema = z.object({
  accountAssignmentTypes: vrtCsvSchema,
  accountIds: vrtCsvSchema,
  accountType: z
    .enum(['ledger', 'bank', 'recurringFee', 'nonPosting'])
    .optional(),
  amount: z.string().trim().min(1).optional(),
  classifications: vrtCsvSchema,
  date: vrtDateRangeSchema,
  endAt: z.string().trim().min(1).optional(),
  entityTypes: vrtCsvSchema,
  excludeTriggerTypes: vrtCsvSchema,
  excludeTypes: vrtCsvSchema,
  ids: vrtCsvSchema,
  includeDetails: z.boolean().optional(),
  ledger: z.enum(['historical', 'trust', 'operating']).optional(),
  limit: vrtLookupLimitSchema,
  listingIds: vrtCsvSchema,
  ownerStatementIds: vrtCsvSchema,
  page: vrtLookupPageSchema,
  party: z.enum(['owners', 'manager']).optional(),
  published: z.boolean().optional(),
  reservationIds: vrtCsvSchema,
  search: z.string().trim().min(1).optional(),
  startAt: z.string().trim().min(1).optional(),
  status: z.enum(['active', 'inactive']).optional(),
  teamId: vrtTeamIdSchema,
  transactionIds: vrtCsvSchema,
  triggerTypes: vrtCsvSchema,
  types: vrtCsvSchema,
});

export type VrtListingsLookupInput = z.infer<typeof vrtListingsLookupSchema>;
export type VrtReservationsLookupInput = z.infer<
  typeof vrtReservationsLookupSchema
>;
export type VrtTransactionsLookupInput = z.infer<
  typeof vrtTransactionsLookupSchema
>;
export type VrtAccountsLookupInput = z.infer<typeof vrtAccountsLookupSchema>;
export type VrtStatementsLookupInput = z.infer<
  typeof vrtStatementsLookupSchema
>;
export type VrtJournalEntriesLookupInput = z.infer<
  typeof vrtJournalEntriesLookupSchema
>;
export type VrtPaginatedLookupOutput = z.infer<
  typeof vrtPaginatedLookupOutputSchema
>;
